import React from 'react';
import { Clock, BadgeCheck, UserPlus, Award, Inbox, TrendingUp, Megaphone, PiggyBank, Globe } from 'lucide-react';
import TiltCard from './TiltCard';

const reasons = [
  {
    icon: Clock,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title: 'Soyez visible 24h/24 et 7j/7',
    text: "Vos futurs clients recherchent vos services sur Internet, souvent avant même de passer un appel. Un site web vous permet d'être trouvé à tout moment, même lorsque votre entreprise est fermée.",
  },
  {
    icon: BadgeCheck,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/20',
    title: 'Gagnez en crédibilité',
    text: "Aujourd'hui, une entreprise sans site internet peut donner une image moins professionnelle. Un site moderne inspire confiance et rassure vos prospects avant leur premier contact.",
  },
  {
    icon: UserPlus,
    color: 'text-amber-400',
    bg: 'bg-amber-500/20',
    title: 'Attirez de nouveaux clients',
    text: "Grâce au référencement sur Google, votre entreprise peut être découverte par des personnes qui recherchent précisément vos services dans votre secteur.",
  },
  {
    icon: Award,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title: 'Présentez votre savoir-faire',
    text: "Mettez en avant vos réalisations, vos prestations, vos avis clients et vos points forts pour convaincre plus facilement vos futurs clients.",
  },
  {
    icon: Inbox,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/20',
    title: 'Recevez des demandes automatiquement',
    text: "Un formulaire de contact, un devis en ligne ou un bouton d'appel permettent à vos visiteurs de vous contacter rapidement, sans contrainte.",
  },
  {
    icon: TrendingUp,
    color: 'text-amber-400',
    bg: 'bg-amber-500/20',
    title: "Prenez l'avantage sur vos concurrents",
    text: "Si vos concurrents sont présents sur Internet et pas vous, ils récupèrent une partie des clients qui vous recherchent. À l'inverse, un site bien conçu vous aide à vous démarquer.",
  },
  {
    icon: Megaphone,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title: 'Une publicité qui travaille en permanence',
    text: "Contrairement aux flyers ou aux annonces ponctuelles, votre site internet continue de promouvoir votre entreprise chaque jour, toute l'année.",
  },
  {
    icon: PiggyBank,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/20',
    title: 'Un investissement rentable',
    text: "Un seul nouveau client obtenu grâce à votre site peut souvent rentabiliser une grande partie de son coût. C'est un outil qui continue à générer des opportunités pendant des années.",
  },
];

const WhyChooseSection = () => {
  return (
    <section id="pourquoi" className="py-20 lg:py-32 bg-gray-950 border-t border-gray-800" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 rounded-full px-4 py-2 mb-6">
            <Globe className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-orange-400 uppercase tracking-wide">Votre présence en ligne</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Pourquoi votre entreprise a besoin d&apos;un site internet
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Un site web n&apos;est plus une option : c&apos;est le meilleur outil pour développer votre activité et convaincre vos futurs clients.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <TiltCard
                key={reason.title}
                data-testid={`why-choose-card-${index}`}
                className="group p-8 bg-gray-900 border border-gray-700 rounded-2xl hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/10"
              >
                <div className={`w-14 h-14 ${reason.bg} rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110`} style={{ transform: 'translateZ(40px)' }}>
                  <Icon className={`w-7 h-7 ${reason.color}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ transform: 'translateZ(25px)' }}>
                  {reason.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" style={{ transform: 'translateZ(15px)' }}>
                  {reason.text}
                </p>
              </TiltCard>
            );
          })}
        </div>

        {/* Closing Statement */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <p className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Votre entreprise mérite une présence en ligne à son image.
          </p>
          <p className="text-lg font-semibold bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
            Offrez à vos clients une première impression professionnelle et donnez-leur envie de vous choisir.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
